// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\ImportBatches.jsx
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";
import { Context } from "../store/appContext";
import getApiBaseUrl from "../utils/api";

const API_URL = getApiBaseUrl();

const STATUS_CONFIG = {
  processed: {
    icon: "bi-check-circle-fill",
    badge: "bg-success-subtle text-success",
    label: "Procesado",
  },
  duplicate: {
    icon: "bi-files-alt",
    badge: "bg-warning-subtle text-warning",
    label: "Duplicado",
  },
  failed: {
    icon: "bi-x-octagon-fill",
    badge: "bg-danger-subtle text-danger",
    label: "Fallido",
  },
  uploaded: {
    icon: "bi-cloud-arrow-up-fill",
    badge: "bg-primary-subtle text-primary",
    label: "Subido",
  },
  rejected: {
    icon: "bi-slash-circle-fill",
    badge: "bg-secondary-subtle text-secondary",
    label: "Rechazado",
  },
};

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

const ImportBatches = ({ refreshKey = 0 }) => {
  const { store, actions } = useContext(Context);
  const token = store?.token || null;
  const isAdmin = store?.role === "admin";
  const [batches, setBatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingBatchId, setDeletingBatchId] = useState(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [batchToDelete, setBatchToDelete] = useState(null);

  const loadBatches = useCallback(
    async (signal) => {
      if (!token) {
        setBatches([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const response = await fetch(`${API_URL}/api/import-batches`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (parseError) {
          payload = null;
        }

        const apiError = await actions.handleApiError(response);
        if (apiError) {
          setBatches([]);
          return;
        }

        if (!payload?.success) {
          actions.showToast(
            payload?.error || payload?.msg || "No se pudo cargar el historial de lotes",
            "error",
          );
          setBatches([]);
          return;
        }

        setBatches(Array.isArray(payload.data) ? payload.data : []);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        console.error("Import batches load error:", error);
        actions.showToast("No se pudo cargar el historial de lotes", "error");
        setBatches([]);
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [token],
  );

  useEffect(() => {
    const controller = new AbortController();

    loadBatches(controller.signal);

    return () => controller.abort();
  }, [loadBatches, refreshKey, listRefreshKey]);

  const handleDeleteBatch = async () => {
    const batch = batchToDelete;
    if (!batch) {
      return;
    }

    setBatchToDelete(null);
    setDeletingBatchId(batch.id);

    try {
      const response = await fetch(`${API_URL}/api/import-batches/${batch.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const payload = await response.json().catch(() => null);

      if (response.status === 401) {
        await actions.handleApiError(response);
        return;
      }

      if (!response.ok || !payload?.success) {
        actions.showToast(
          payload?.error || payload?.msg || "No se pudo eliminar el lote",
          "error",
        );
        return;
      }

      const deletedStagingRows = payload.data?.deleted_staging_rows ?? 0;
      const deletedCleanRows = payload.data?.deleted_clean_rows ?? 0;
      actions.showToast(
        `Lote #${batch.id} eliminado. Staging: ${deletedStagingRows}. Clean: ${deletedCleanRows}.`,
        "success",
      );
      setListRefreshKey((currentValue) => currentValue + 1);
    } catch (error) {
      console.error("Import batch delete error:", error);
      actions.showToast("No se pudo eliminar el lote", "error");
    } finally {
      setDeletingBatchId(null);
    }
  };

  return (
    <div className="card shadow-sm mt-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-1">Lotes importados</h5>
            <p className="text-muted mb-0">
              Histórico de archivos subidos y su estado de procesamiento.
            </p>
          </div>
        </div>

        {isLoading && <div className="text-muted">Cargando lotes...</div>}

        {!isLoading && batches.length === 0 && (
          <div className="alert alert-light border mb-0" role="alert">
            No hay lotes registrados todavía.
          </div>
        )}

        {!isLoading && batches.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Archivo</th>
                  <th>Fecha</th>
                  <th>Estado</th>
                  <th className="text-end">Filas insertadas</th>
                  <th className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => {
                  const config =
                    STATUS_CONFIG[batch.status] || STATUS_CONFIG.rejected;
                  const duplicateReference =
                    batch.duplicate_of_batch_id &&
                    `Duplicado del lote #${batch.duplicate_of_batch_id}`;
                  const notesText = batch.notes || duplicateReference;
                  return (
                    <tr key={batch.id}>
                      <td>
                        <div className="fw-semibold">
                          {batch.source_filename}
                        </div>
                        {notesText && (
                          <div className="small text-muted">{notesText}</div>
                        )}
                      </td>
                      <td>{formatDateTime(batch.imported_at)}</td>
                      <td>
                        <span className={`badge ${config.badge}`}>
                          <i className={`bi ${config.icon} me-1`}></i>
                          {config.label}
                        </span>
                      </td>
                      <td className="text-end">{batch.rows_inserted ?? 0}</td>
                      <td className="text-end">
                        <div className="d-inline-flex gap-2">
                          <Link
                            className="btn btn-outline-primary btn-sm"
                            to={`/import-batches/${batch.id}`}
                          >
                            <i className="bi bi-eye me-1"></i>
                            Ver detalle
                          </Link>
                          {isAdmin && (
                            <button
                              className="btn btn-outline-danger btn-sm"
                              type="button"
                              disabled={deletingBatchId === batch.id}
                              onClick={() => setBatchToDelete(batch)}
                            >
                              <i className="bi bi-trash me-1"></i>
                              {deletingBatchId === batch.id
                                ? "Eliminando..."
                                : "Eliminar lote"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={Boolean(batchToDelete)}
        title="Eliminar lote importado"
        message={`Eliminar «${batchToDelete?.source_filename}»: se borrarán sus filas staging y sus movimientos clean asociados. Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar lote"
        cancelLabel="Cancelar"
        danger
        onConfirm={handleDeleteBatch}
        onCancel={() => setBatchToDelete(null)}
      />
    </div>
  );
};

export default ImportBatches;
